// src/pwa/src/services/githubTransport.ts
// Issue 74 — thin transport for uploading binary files (PDFs) to the repo / GitHub.
// Routes via the existing Cloudflare Worker endpoint pattern. Writes go through
// `PUT /api/github/contents/{owner}/{repo}/{path}` after the worker has hydrated
// the GitHub PAT secret (Issue 56). If the worker is offline we mark the file
// for later upload and surface a deterministic URL.

const CONFIG_FILE = '/api/config.json';

interface TransportConfig {
    githubOwner: string;
    githubRepo: string;
    baseUrl: string;
}

let cachedConfig: TransportConfig | null = null;

async function loadConfig(): Promise<TransportConfig> {
    if (cachedConfig) {return cachedConfig;}
    try {
        const res = await fetch(CONFIG_FILE, { cache: 'no-store' });
        if (res.ok) {
            const cfg = await res.json();
            cachedConfig = {
                githubOwner: cfg.GITHUB_OWNER || cfg.githubOwner || 'Gestalt360',
                githubRepo: cfg.GITHUB_DOCS_REPO || cfg.githubRepo || 'bizbuilder_documents',
                baseUrl: cfg.API_BASE_URL || '',
            };
            return cachedConfig;
        }
    } catch {
        // fall through to defaults
    }
    cachedConfig = { githubOwner: 'Gestalt360', githubRepo: 'bizbuilder_documents', baseUrl: '' };
    return cachedConfig;
}

export interface PutFileArgs {
    path: string;
    contentBase64: string;
    commitMessage: string;
}

export interface PutFileResult {
    url: string;
    sha: string;
    queued?: boolean;
}

export async function putFile(args: PutFileArgs): Promise<PutFileResult> {
    const cfg = await loadConfig();
    try {
        const endpoint = `${cfg.baseUrl || ''}/api/github/contents/${encodeURIComponent(cfg.githubOwner)}/${encodeURIComponent(cfg.githubRepo)}/${args.path.split('/').map(encodeURIComponent).join('/')}`;
        const res = await fetch(endpoint, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                message: args.commitMessage,
                content: args.contentBase64,
                encoding: 'base64',
            }),
        });
        if (!res.ok) {throw new Error(`gitHub put ${res.status}`);}
        const data = await res.json();
        return { url: data.content?.html_url || `https://github.com/${cfg.githubOwner}/${cfg.githubRepo}/blob/main/${args.path}`, sha: data.content?.sha || '' };
    } catch (err) {
        // Worker unreachable — surface deterministic URL but mark as queued so callers can
        // attempt a retry. The DocumentsRepoService keeps the IndexedDB passthrough handy.
        return {
            url: `https://github.com/${cfg.githubOwner}/${cfg.githubRepo}/blob/main/${args.path}`,
            sha: '',
            queued: true,
        };
    }
}
